import { FloraTypography } from '@grupoboticario/flora-react';
import {
  ArrowLeftIcon,
  ExclamationMarkHexagonIcon,
  BoxIcon,
  SmileCircleIcon,
  ThumbUpOutlineIcon,
} from '@grupoboticario/flora-react-icons';
import { app, cart, combo, type Cart } from 'core';
import type { DataTracking } from 'core/middlewares/trackingGA4';
import { TextBlock } from 'ui/components';
import { formatString } from 'core/utilities';
import {
  ButtonGhost,
  ButtonIcon,
  ButtonSolid,
  Container,
  IconWrapper,
  Layout,
} from 'ui/components/primitives';
import { getShippingModelName } from './helpers';
import { Drawer } from './ShippingModelDrawser.styles';
import { isMobile } from 'core/use-cases/app/helpers';

const ShippingModelProductNotFoundContainer = () => {
  const { closeProductNotFoundShippingModel, openShippingModel } = app.commands.useAppCommand();
  const { trackClick } = app.commands.useAppCommand();

  const isProductNotFoundShippingActive = app.hooks.useApp('isProductNotFoundShippingActive');
  const cartItemToUpdate = cart.hooks.useCartPropertyByKey('itemUpdate');

  const {
    data: { comboCode },
  } = combo.hooks.useCombo();

  const {
    shippingModel: { businessModelCode = 0 },
  } = cart.hooks.useCartShippingModel();

  const {
    meta: { productCode },
  } = cartItemToUpdate as Cart['itemUpdate'];

  const selected_shippingmodel = getShippingModelName(businessModelCode);

  const handleClickClose = (e: React.UIEvent<HTMLButtonElement, UIEvent>) => {
    closeProductNotFoundShippingModel();

    const trackData = {
      eventAction: 'compre e retire',
      eventLabel: 'Ver produtos para receber',
    } as DataTracking;

    trackClick(e, 'event', trackData);
  };

  const handleClickOpenShipping = (e: React.UIEvent<HTMLButtonElement, UIEvent>) => {
    openShippingModel();

    const trackData = {
      eventAction: 'compre e retire',
      eventLabel: 'Alterar modelo de entrega',
    } as DataTracking;

    trackClick(e, 'event', trackData);
  };

  const hasSelectedShippingModel = selected_shippingmodel !== undefined;

  return (
    <>
      <Drawer.Overlay onClick={handleClickClose} visible={isProductNotFoundShippingActive} />
      <Drawer.Container data-open={isProductNotFoundShippingActive}>
        <Drawer.Component>
          <ButtonIcon onClick={handleClickClose}>
            <ArrowLeftIcon size={24} />
          </ButtonIcon>
          <FloraTypography
            as={'h6'}
            fontSize="subtitle"
            viewport={isMobile() ? 'mobile' : 'desktop'}
            css={{ margin: 0 }}
          >
            Modelo de entrega
          </FloraTypography>
        </Drawer.Component>

        <Layout>
          <Container marginTop="sp-10" marginBottom="sp-0" size="full" horizontalAlign="center">
            <IconWrapper textAlign="center">
              <ExclamationMarkHexagonIcon size={64} />
            </IconWrapper>
          </Container>
        </Layout>

        <Container marginBottom="sp-2" marginTop="sp-4" size="2/3">
          <FloraTypography
            as={'h6'}
            fontSize="subtitle"
            viewport={isMobile() ? 'mobile' : 'desktop'}
            css={{ margin: 0, textAlign: 'center', color: '$backgroundAltPrimary' }}
          >
            {hasSelectedShippingModel
              ? formatString('Produto {query} sem estoque para {selected_shippingmodel}!', {
                  query: productCode || comboCode,
                  selected_shippingmodel,
                })
              : formatString('Produto {query} sem estoque!', { query: productCode || comboCode })}
          </FloraTypography>
        </Container>

        <Container marginBottom="sp-2" marginTop="sp-4" size="full">
          <TextBlock
            text="O estoque pode variar de acordo com o modelo de entrega"
            textAlign="left"
            variant="bodySmallShort"
            icon={<BoxIcon size={12} />}
          />
        </Container>

        <Container marginBottom="sp-2" marginTop="sp-2" size="full">
          <TextBlock
            text="Experimente alterar o modelo de entrega para verificar a disponibilidade de estoque"
            textAlign="left"
            variant="bodySmallShort"
            icon={<SmileCircleIcon size={12} />}
          />
        </Container>

        {hasSelectedShippingModel && (
          <Container marginBottom="sp-2" marginTop="sp-2" size="full">
            <TextBlock
              text={`Ou veja todos os produtos disponíveis para ${selected_shippingmodel}`}
              textAlign="left"
              variant="bodySmallShort"
              icon={<ThumbUpOutlineIcon size={12} />}
            />
          </Container>
        )}

        <Container marginBottom="sp-2" marginTop="sp-6" size="full">
          <ButtonSolid onClick={handleClickOpenShipping} stretch>
            Alterar modelo de entrega
          </ButtonSolid>
        </Container>

        <Container marginBottom="sp-2" marginTop="sp-4" size="full">
          <ButtonGhost onClick={handleClickClose} stretch>
            Ver produtos
          </ButtonGhost>
        </Container>
      </Drawer.Container>
    </>
  );
};

export default ShippingModelProductNotFoundContainer;
